import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  CheckCircle, 
  Shield, 
  Scale,
  Building2,
  Code,
  Server,
  Users,
  Clock,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LogoFullHorizontal } from '../components/Logo';

export default function LicenseAgreement() {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'grant',
      title: '1. License Grant',
      icon: FileText,
      content: [
        {
          subtitle: '1.1 Grant of License',
          text: 'Subject to the terms of this Agreement and upon full payment of the applicable License Fee, Licensor hereby grants to Licensee a perpetual, non-exclusive, non-transferable license to use, modify, and deploy the Software ("MyHR Orbit") for Licensee\'s internal business purposes.'
        },
        {
          subtitle: '1.2 Scope of License',
          text: 'This license permits Licensee to: (a) Install and run the Software on Licensee\'s own servers or cloud infrastructure; (b) Modify the source code to suit Licensee\'s business requirements; (c) Create derivative works based on the Software for internal use; (d) Deploy the Software for unlimited internal users within Licensee\'s organization.'
        },
        {
          subtitle: '1.3 Non-Exclusive Nature',
          text: 'Licensee acknowledges that this license is non-exclusive, and Licensor retains the right to license identical or substantially similar software to other parties without restriction.'
        }
      ]
    },
    {
      id: 'restrictions',
      title: '2. License Restrictions',
      icon: AlertCircle,
      content: [
        {
          subtitle: '2.1 Prohibited Actions',
          text: 'Licensee shall NOT: (a) Sublicense, sell, resell, rent, lease, or distribute the Software to third parties; (b) Offer the Software as a hosted service (SaaS) to third parties; (c) Remove or alter any proprietary notices, labels, or marks on the Software; (d) Use the Software to develop a competing product for commercial sale.'
        },
        {
          subtitle: '2.2 Third-Party Access',
          text: 'Licensee may permit contractors and consultants to access the Software solely for the purpose of providing services to Licensee, provided such parties agree to confidentiality obligations no less protective than those contained herein.'
        }
      ]
    },
    {
      id: 'ip',
      title: '3. Intellectual Property',
      icon: Shield,
      content: [
        {
          subtitle: '3.1 Ownership',
          text: 'Licensor retains all right, title, and interest in and to the Software, including all intellectual property rights therein. This Agreement does not transfer any ownership rights to Licensee.'
        },
        {
          subtitle: '3.2 Modifications',
          text: 'Any modifications or derivative works created by Licensee shall be owned by Licensee, but the underlying Software and original code remain the property of Licensor.'
        },
        {
          subtitle: '3.3 Feedback',
          text: 'If Licensee provides any feedback, suggestions, or improvements to Licensor, Licensor may use such feedback without any obligation to Licensee.'
        }
      ]
    },
    {
      id: 'payment',
      title: '4. Payment Terms',
      icon: Scale,
      content: [
        {
          subtitle: '4.1 License Fee',
          text: 'Licensee shall pay the one-time License Fee as specified in the applicable pricing tier (Startup: RM 45,000 / Business: RM 95,000 / Enterprise: RM 200,000). The License Fee is non-refundable once the Software has been delivered.'
        },
        {
          subtitle: '4.2 Payment Schedule',
          text: 'For installment payments (where applicable): 50% upon signing, 50% upon delivery of source code. Full source code access is granted only upon receipt of final payment.'
        },
        {
          subtitle: '4.3 Taxes',
          text: 'All fees are exclusive of applicable taxes. Licensee is responsible for all taxes, duties, and governmental charges (excluding Licensor\'s income tax) arising from this Agreement.'
        }
      ]
    },
    {
      id: 'support',
      title: '5. Support & Maintenance',
      icon: Clock,
      content: [
        {
          subtitle: '5.1 Included Support',
          text: 'Each license tier includes a defined support period: Startup (1 month), Business (3 months), Enterprise (6 months). Support includes bug fixes, email support, and reasonable technical assistance.'
        },
        {
          subtitle: '5.2 Extended Support',
          text: 'Additional support beyond the included period may be purchased separately at Licensor\'s then-current rates.'
        },
        {
          subtitle: '5.3 No Obligation',
          text: 'After the support period expires, Licensor has no obligation to provide updates, patches, or support services unless separately agreed.'
        }
      ]
    },
    {
      id: 'warranty',
      title: '6. Warranty & Disclaimer',
      icon: HelpCircle,
      content: [
        {
          subtitle: '6.1 Limited Warranty',
          text: 'Licensor warrants that the Software will perform substantially in accordance with the documentation for a period of thirty (30) days from delivery. Licensor\'s sole obligation for breach of this warranty is to use reasonable efforts to correct the non-conformity.'
        },
        {
          subtitle: '6.2 Disclaimer',
          text: 'EXCEPT AS EXPRESSLY SET FORTH HEREIN, THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. LICENSOR DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.'
        },
        {
          subtitle: '6.3 Limitation of Liability',
          text: 'IN NO EVENT SHALL LICENSOR BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. LICENSOR\'S TOTAL LIABILITY SHALL NOT EXCEED THE LICENSE FEE PAID BY LICENSEE.'
        }
      ]
    },
    {
      id: 'termination',
      title: '7. Term & Termination',
      icon: Building2,
      content: [
        {
          subtitle: '7.1 Term',
          text: 'This Agreement is effective upon full payment and continues in perpetuity unless terminated as provided herein.'
        },
        {
          subtitle: '7.2 Termination for Breach',
          text: 'Either party may terminate this Agreement upon thirty (30) days written notice if the other party materially breaches this Agreement and fails to cure such breach within the notice period.'
        },
        {
          subtitle: '7.3 Effect of Termination',
          text: 'Upon termination for Licensee\'s breach, Licensee must cease all use of the Software and destroy all copies. Termination does not entitle Licensee to any refund of fees paid.'
        }
      ]
    },
    {
      id: 'general',
      title: '8. General Provisions',
      icon: Code,
      content: [
        {
          subtitle: '8.1 Governing Law',
          text: 'This Agreement shall be governed by the laws of Malaysia. Any disputes shall be resolved in the courts of Kuala Lumpur, Malaysia.'
        },
        {
          subtitle: '8.2 Entire Agreement',
          text: 'This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations, representations, or agreements relating to the subject matter hereof.'
        },
        {
          subtitle: '8.3 Amendment',
          text: 'This Agreement may only be amended by a written instrument signed by both parties.'
        },
        {
          subtitle: '8.4 Severability',
          text: 'If any provision of this Agreement is held invalid or unenforceable, the remaining provisions shall continue in full force and effect.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <LogoFullHorizontal className="h-8" />
            </div>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-full px-6"
            >
              Get Quote
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="pt-28 pb-16 px-6 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-semibold mb-6">
            <Scale className="h-4 w-4 mr-2" />
            Legal Document
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Perpetual Source Code License Agreement
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Non-Exclusive Software License Agreement for MyHR Orbit
          </p>
          <p className="text-sm text-slate-500 mt-4">
            Last updated: February 2026 • Version 1.0
          </p>
        </div>
      </div>

      {/* Quick Summary */}
      <div className="py-12 px-6 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Key Points Summary
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Perpetual License</p>
                  <p className="text-sm text-slate-400">Use the software forever with no expiration</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Non-Exclusive</p>
                  <p className="text-sm text-slate-400">Same software may be licensed to others</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Full Source Code</p>
                  <p className="text-sm text-slate-400">Complete access to modify as needed</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">One-Time Payment</p>
                  <p className="text-sm text-slate-400">No recurring fees or subscriptions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">No Resale Rights</p>
                  <p className="text-sm text-slate-400">Cannot sublicense or sell to others</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">No SaaS Hosting</p>
                  <p className="text-sm text-slate-400">Cannot offer as hosted service to third parties</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="py-8 px-6 border-b border-slate-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-semibold text-white mb-4">Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors py-1"
              >
                <section.icon className="h-4 w-4" />
                <span className="text-sm">{section.title}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Agreement Content */}
      <div className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Preamble */}
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-300 leading-relaxed">
              This Perpetual Source Code License Agreement ("Agreement") is entered into as of the date of 
              purchase ("Effective Date") by and between <strong className="text-white">MyHR Orbit</strong> ("Licensor") 
              and the purchasing entity ("Licensee").
            </p>
            <p className="text-slate-300 leading-relaxed">
              WHEREAS, Licensor has developed a human resource management software system known as "MyHR Orbit" 
              (the "Software"); and WHEREAS, Licensee desires to obtain a license to use the Software pursuant 
              to the terms and conditions set forth herein.
            </p>
            <p className="text-slate-300 leading-relaxed">
              NOW, THEREFORE, in consideration of the mutual covenants and agreements contained herein, and for 
              other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, 
              the parties agree as follows:
            </p>
          </div>

          {/* Sections */}
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.id} id={section.id} className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Icon className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="space-y-6 pl-13">
                  {section.content.map((item, index) => (
                    <div key={index} className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-emerald-400 mb-2">{item.subtitle}</h3>
                      <p className="text-slate-300 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Signature Block */}
          <div className="border-t border-slate-700 pt-12 mt-12">
            <h2 className="text-2xl font-bold text-white mb-6">Agreement Acceptance</h2>
            <p className="text-slate-300 mb-8">
              By purchasing a license and making payment, Licensee acknowledges that they have read, understood, 
              and agree to be bound by the terms and conditions of this Agreement.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">LICENSOR</h3>
                <p className="text-slate-400 text-sm mb-2">MyHR Orbit</p>
                <p className="text-slate-400 text-sm mb-2">Kuala Lumpur, Malaysia</p>
                <p className="text-slate-400 text-sm">sales@myhrorbit.com</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">LICENSEE</h3>
                <p className="text-slate-400 text-sm mb-2">[Company Name]</p>
                <p className="text-slate-400 text-sm mb-2">[Address]</p>
                <p className="text-slate-400 text-sm">[Contact Email]</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 px-6 bg-gradient-to-r from-emerald-900/30 via-teal-900/30 to-cyan-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-slate-300 mb-8">
            Contact us to discuss your requirements and receive a custom quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/')}
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-full px-8"
            >
              Request License Quote
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              size="lg"
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8"
            >
              Try Demo First
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-950 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} MyHR Orbit. All rights reserved. This agreement template is provided for informational purposes. 
          Consult with a legal professional for your specific needs.
        </div>
      </footer>
    </div>
  );
}
